import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import MobileActionMenu from "../../components/MobileActionMenu";
import ModeloModal from "./ModeloModal";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { listContratoTemplates, deleteContratoTemplate } from "../../services/contratoService";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: "transparent",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: "1px solid #e0e0e0",
    flexWrap: "wrap",
    gap: 12,
    [theme.breakpoints.down("sm")]: { padding: "12px 16px", flexDirection: "column", alignItems: "stretch" },
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  headerTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#1a1a1a",
    [theme.breakpoints.down("sm")]: { fontSize: "1.2rem" },
  },
  headerSubtitle: {
    fontSize: "0.875rem",
    color: "#666",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: { flexDirection: "column", alignItems: "stretch" },
  },
  searchField: {
    backgroundColor: "#fff",
    borderRadius: 8,
    minWidth: 220,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#1976d2" },
    },
    [theme.breakpoints.down("sm")]: { minWidth: "unset", width: "100%" },
  },
  addButton: {
    borderRadius: 8,
    padding: "6px 20px",
    textTransform: "none",
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: { width: "100%" },
  },
  content: {
    padding: "16px 24px",
    [theme.breakpoints.down("sm")]: { padding: "12px 12px 16px" },
  },
  tableWrapper: {
    backgroundColor: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  tableScroll: { overflowX: "auto", width: "100%" },
  tableHead: {
    backgroundColor: "var(--sidebar-color, #1e293b)",
    "& th": {
      color: "#cbd5e1",
      fontWeight: 600,
      fontSize: "0.8rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      borderBottom: "none",
      padding: "14px 16px",
    },
  },
  tableBody: {
    "& td": {
      padding: "12px 16px",
      fontSize: "0.875rem",
      color: "#334155",
      borderBottom: "1px solid #f1f5f9",
    },
    "& tr:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  actionBtn: {
    minWidth: "auto",
    padding: "4px 8px",
    borderRadius: 6,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    color: "#999",
  },
}));

export default function ContratoModelosPage() {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const sidebarColor = theme.palette.primary.main || "#1e293b";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [templates, setTemplates] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [modal, setModal] = useState({ open: false, id: null });

  const load = async () => {
    try {
      const data = await listContratoTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch {
      setSnack({ open: true, message: "Erro ao carregar modelos.", severity: "error" });
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleDelete = async () => {
    try {
      await deleteContratoTemplate(deleteDialog.id);
      setSnack({ open: true, message: "Modelo excluído.", severity: "success" });
      setDeleteDialog({ open: false, id: null });
      load();
    } catch {
      setSnack({ open: true, message: "Erro ao excluir.", severity: "error" });
    }
  };

  const filtered = templates.filter((t) =>
    !searchParam ||
    t.name.toLowerCase().includes(searchParam.toLowerCase()) ||
    (t.description || "").toLowerCase().includes(searchParam.toLowerCase())
  );

  return (
    <Box className={classes.root} style={{ "--sidebar-color": sidebarColor }}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Box>
            <Typography className={classes.headerTitle}>Modelos de Contrato</Typography>
            <Typography className={classes.headerSubtitle}>
              {templates.length} modelo(s) cadastrado(s)
            </Typography>
          </Box>
        </Box>

        <Box className={classes.headerRight}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Buscar modelos..."
            value={searchParam}
            onChange={(e) => { const v = e.target.value; setSearchParam(v); }}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.addButton}
            startIcon={<AddIcon />}
            onClick={() => setModal({ open: true, id: null })}
          >
            Novo Modelo
          </Button>
        </Box>
      </Box>

      {/* Conteúdo */}
      <Box className={classes.content}>
        <Box className={classes.tableWrapper}>
          <Box className={classes.tableScroll}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {filtered.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {t.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{t.description || "—"}</TableCell>
                  <TableCell align="right">
                    {isMobile ? (
                      <MobileActionMenu actions={[
                        { label: "Editar", icon: <EditIcon fontSize="small" />, onClick: () => setModal({ open: true, id: t.id }) },
                        { label: "Usar em novo contrato", icon: <FileCopyIcon fontSize="small" />, onClick: () => history.push(`/contratos/novo?templateId=${t.id}`) },
                        { label: "Excluir", icon: <DeleteIcon fontSize="small" />, onClick: () => setDeleteDialog({ open: true, id: t.id }), color: "#ef4444" },
                      ]} />
                    ) : (
                      <Box display="flex" justifyContent="flex-end" style={{ gap: 2 }}>
                        <Tooltip title="Editar">
                          <IconButton size="small" className={classes.actionBtn} onClick={() => setModal({ open: true, id: t.id })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Usar em novo contrato">
                          <IconButton size="small" className={classes.actionBtn} onClick={() => history.push(`/contratos/novo?templateId=${t.id}`)}>
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" className={classes.actionBtn} onClick={() => setDeleteDialog({ open: true, id: t.id })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Box className={classes.emptyState}>
                      <Typography variant="body2">
                        {searchParam ? "Nenhum modelo encontrado para a busca." : "Nenhum modelo cadastrado."}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </Box>
        </Box>
      </Box>

      <ModeloModal
        open={modal.open}
        templateId={modal.id}
        onClose={(reload) => {
          setModal({ open: false, id: null });
          if (reload) load();
        }}
      />

      {/* Dialog de exclusão */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>Excluir Modelo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta ação não pode ser desfeita. Confirma a exclusão do modelo?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancelar</Button>
          <Button color="secondary" onClick={handleDelete}>Excluir</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
